import type {
  ConvertQuoteSplitDto,
  QuoteLineItemResponseDto,
  QuoteResponseDto,
} from '@trellis/api'
import { Alert, AlertDescription } from '@trellis/roots/alert'
import { Button } from '@trellis/roots/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@trellis/roots/dialog'
import {
  DatePicker,
  QuantitySelector,
  usePercentageSplitRows,
} from '@trellis/roots/forms'
import { Input } from '@trellis/roots/input'
import { Label } from '@trellis/roots/label'
import { Slider } from '@trellis/roots/slider'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@trellis/roots/tabs'
import { Typography } from '@trellis/roots/typography'
import { calendarDateToUtcMidnight, getQuantityStepForUnitType } from '@trellis/shared'
import { format, parseISO } from 'date-fns'
import { Info, Minus, Plus, Receipt, Split, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { RemindersSection } from '@/components/reminders'
import {
  useConvertQuoteSplitMutation,
  useDefaultDueDate,
  useOrganizationToday,
} from '@/hooks'
import { cn } from '@/lib/utils'

const MIN_SPLITS = 2
const MAX_SPLITS = 5

interface SplitRow {
  label: string
  /** ISO `yyyy-MM-dd` date string. */
  issueDate: string | undefined
  /** ISO `yyyy-MM-dd` date string. */
  dueDate: string | undefined
}

type SplitMode = ConvertQuoteSplitDto['mode']

interface SplitQuoteDialogProps {
  quote: QuoteResponseDto
  lineItems: QuoteLineItemResponseDto[]
  open: boolean
  onOpenChange: (open: boolean) => void
}

function defaultRowLabel(index: number): string {
  if (index === 0) return 'Deposit'
  return `Milestone ${index + 1}`
}

function resizeRows(
  rows: SplitRow[],
  nextLength: number,
  defaultIssueDate: string,
  defaultDueDate: string,
): SplitRow[] {
  if (rows.length === nextLength) return rows
  if (rows.length < nextLength) {
    const next = [...rows]
    while (next.length < nextLength)
      next.push({
        label: defaultRowLabel(next.length),
        issueDate: defaultIssueDate,
        dueDate: defaultDueDate,
      })
    return next
  }
  return rows.slice(0, nextLength)
}

function evenIntegerSplit(total: number, count: number): number[] {
  const base = Math.floor(total / count)
  const remainder = total - base * count
  return Array.from({ length: count }, (_, index) =>
    index < remainder ? base + 1 : base,
  )
}

function evenQuantitySplit(total: number, count: number): number[] {
  const base = Math.floor((total / count) * 10000) / 10000
  const values = Array(count).fill(base)
  values[count - 1] = Number((total - base * (count - 1)).toFixed(4))
  return values
}

/**
 * Mirrors the cent-level rounding QuotesService.convertSplit() does per invoice: if every
 * percentage's share of the total already lands on a whole cent, no invoice needs its
 * amount nudged to reconcile — so the last invoice won't actually absorb a remainder.
 */
function percentageSplitHasRoundingRemainder(
  total: number,
  percentages: number[],
): boolean {
  const totalCents = Math.round(total * 100)
  const allocatedCents = percentages.reduce(
    (sum, percentage) => sum + Math.round((totalCents * percentage) / 100),
    0,
  )
  return allocatedCents !== totalCents
}

function resizeAllocations(
  current: Record<string, number[]>,
  items: QuoteLineItemResponseDto[],
  nextLength: number,
): Record<string, number[]> {
  const next: Record<string, number[]> = {}
  for (const item of items) {
    const existing = current[item.id]
    next[item.id] =
      existing && existing.length === nextLength
        ? existing
        : evenQuantitySplit(item.quantity, nextLength)
  }
  return next
}

export function SplitQuoteDialog({
  quote,
  lineItems,
  open,
  onOpenChange,
}: SplitQuoteDialogProps) {
  const organizationToday = useOrganizationToday()
  const defaultDueDate = useDefaultDueDate(organizationToday)
  const [mode, setMode] = useState<SplitMode>('PERCENTAGE')
  const [step, setStep] = useState<'configure' | 'confirm'>('configure')
  const [percentages, setPercentages] = useState<number[]>(
    evenIntegerSplit(100, MIN_SPLITS),
  )
  const [splitRows, setSplitRows] = useState<SplitRow[]>(() =>
    resizeRows([], MIN_SPLITS, organizationToday, defaultDueDate),
  )
  const [remindersEnabled, setRemindersEnabled] = useState(true)
  const [reminderScheduleId, setReminderScheduleId] = useState<string | null>(null)
  const [allocations, setAllocations] = useState<Record<string, number[]>>(() =>
    resizeAllocations({}, lineItems, MIN_SPLITS),
  )

  const mutation = useConvertQuoteSplitMutation()

  /* The dialog stays mounted (QuoteDetailPage renders it unconditionally) across
   * navigations that don't remount the page — e.g. the superseded-quote or
   * revision-history links, which route between two /quotes/:id URLs without a full
   * remount. Reset to defaults whenever the quote being split changes so a draft split
   * left mid-edit on one quote never leaks into a different quote's dialog session.
   */
  useEffect(() => {
    setMode('PERCENTAGE')
    setStep('configure')
    setPercentages(evenIntegerSplit(100, MIN_SPLITS))
    setSplitRows(resizeRows([], MIN_SPLITS, organizationToday, defaultDueDate))
    setAllocations(resizeAllocations({}, lineItems, MIN_SPLITS))
    setRemindersEnabled(true)
  }, [quote.id])

  /**
   * UsePercentageSplitRows inserts/removes at whichever index its own redistribution math
   * picks (splitting the largest share in place, not necessarily appending at the end),
   * so splitRows (labels/dates) must be spliced at that same index — resizing by length
   * alone would silently reassign an existing row's label/date to a different
   * percentage.
   */
  function handlePercentageRowAdded(insertIndex: number) {
    const nextLength = percentages.length + 1
    setSplitRows((current) => {
      const next = [...current]
      next.splice(insertIndex, 0, {
        label: defaultRowLabel(insertIndex),
        issueDate: organizationToday,
        dueDate: defaultDueDate,
      })
      return next
    })
    setAllocations((current) => resizeAllocations(current, lineItems, nextLength))
  }

  function handlePercentageRowRemoved(index: number) {
    setSplitRows((current) => current.filter((_, rowIndex) => rowIndex !== index))
    setAllocations((current) => {
      const next: Record<string, number[]> = {}
      for (const item of lineItems) {
        const existing =
          current[item.id] ?? evenQuantitySplit(item.quantity, percentages.length)
        next[item.id] = existing.filter((_, valueIndex) => valueIndex !== index)
      }
      return next
    })
  }

  const {
    total: percentageTotal,
    canAdd: canAddPercentageSplit,
    canRemove: canRemovePercentageSplit,
    handleDrag: handlePercentageDrag,
    handleAdd: handleAddPercentageSplit,
    handleRemove: handleRemovePercentageSplit,
  } = usePercentageSplitRows({
    value: percentages,
    onChange: setPercentages,
    onAdd: handlePercentageRowAdded,
    onRemove: handlePercentageRowRemoved,
    min: MIN_SPLITS,
    max: MAX_SPLITS,
  })

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) setStep('configure')
    onOpenChange(nextOpen)
  }

  function handleAddSplitRow() {
    if (splitRows.length >= MAX_SPLITS) return
    const nextLength = splitRows.length + 1
    setSplitRows((current) =>
      resizeRows(current, nextLength, organizationToday, defaultDueDate),
    )
    setAllocations((current) => resizeAllocations(current, lineItems, nextLength))
    setPercentages(evenIntegerSplit(100, nextLength))
  }

  function handleRemoveSplitRow(index: number) {
    if (splitRows.length <= MIN_SPLITS) return
    const nextLength = splitRows.length - 1
    setSplitRows((current) => current.filter((_, rowIndex) => rowIndex !== index))
    setAllocations((current) => resizeAllocations(current, lineItems, nextLength))
    setPercentages(evenIntegerSplit(100, nextLength))
  }

  function handleAllocationChange(lineItemId: string, index: number, nextValue: number) {
    setAllocations((current) => {
      const values = [...(current[lineItemId] ?? [])]
      values[index] = Math.max(0, nextValue)
      return { ...current, [lineItemId]: values }
    })
  }

  function updateRowLabel(index: number, label: string) {
    setSplitRows((current) =>
      current.map((row, rowIndex) => (rowIndex === index ? { ...row, label } : row)),
    )
  }

  function updateRowIssueDate(index: number, issueDate: string) {
    setSplitRows((current) =>
      current.map((row, rowIndex) => (rowIndex === index ? { ...row, issueDate } : row)),
    )
  }

  function updateRowDueDate(index: number, dueDate: string) {
    setSplitRows((current) =>
      current.map((row, rowIndex) => (rowIndex === index ? { ...row, dueDate } : row)),
    )
  }

  const rowLabelsValid = splitRows.every((row) => row.label.trim().length > 0)
  const rowDatesValid = splitRows.every(
    (row) => row.issueDate !== undefined && row.dueDate !== undefined,
  )
  const percentagesValid =
    mode !== 'PERCENTAGE' || percentages.reduce((sum, value) => sum + value, 0) === 100
  const lineItemQuantitiesValid =
    mode !== 'LINE_ITEM' ||
    lineItems.every((item) => {
      const allocated = (allocations[item.id] ?? []).reduce(
        (sum, value) => sum + value,
        0,
      )
      return Math.abs(allocated - item.quantity) < 0.0001
    })
  const canReview =
    rowLabelsValid && rowDatesValid && percentagesValid && lineItemQuantitiesValid
  const showPercentageRoundingNotice =
    mode === 'PERCENTAGE' && percentageSplitHasRoundingRemainder(quote.total, percentages)

  function buildSplitsPayload(): ConvertQuoteSplitDto['splits'] {
    return splitRows.map((row, index) => ({
      label: row.label.trim(),
      issueDate: calendarDateToUtcMidnight(row.issueDate!).toISOString(),
      dueDate: calendarDateToUtcMidnight(row.dueDate!).toISOString(),
      ...(mode === 'PERCENTAGE'
        ? { percentage: percentages[index] }
        : {
            lineItemAllocations: lineItems
              .map((item) => ({
                quoteLineItemId: item.id,
                quantity: allocations[item.id]?.[index] ?? 0,
              }))
              .filter((allocation) => allocation.quantity > 0),
          }),
    }))
  }

  function handleSubmit() {
    if (!canReview) return
    mutation.mutate(
      {
        id: quote.id,
        data: {
          mode,
          splits: buildSplitsPayload(),
          remindersEnabled,
          reminderScheduleId,
        },
      },
      {
        onSuccess: () => {
          toast.success('Quote split into milestone invoices')
          handleOpenChange(false)
        },
        onError: (caughtError) => {
          const message =
            caughtError instanceof Error ? caughtError.message : 'Failed to split quote'
          toast.error('Failed to split quote', { description: message })
        },
      },
    )
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="grid max-h-[85vh] grid-rows-[auto_1fr_auto] overflow-hidden sm:max-w-205">
        {step === 'configure' ? (
          <>
            <DialogHeader>
              <DialogTitle>Split into Milestone Invoices</DialogTitle>
              <DialogDescription>
                Convert this quote into multiple invoices. Each milestone keeps its label,
                issue and due dates, and share of the total together.
              </DialogDescription>
            </DialogHeader>

            <div className="min-w-0 space-y-6 overflow-y-auto py-2">
              <Tabs value={mode} onValueChange={(value) => setMode(value as SplitMode)}>
                <div className="space-y-3">
                  <div className="space-y-1">
                    <Typography variant="h5" as="span" className="font-semibold">
                      Split by
                    </Typography>
                    <Typography variant="body-sm" color="muted">
                      Split invoice by a percentage of the quote total or based on
                      specific line item quantities.
                    </Typography>
                  </div>
                  <TabsList>
                    <TabsTrigger value="PERCENTAGE">Percentage</TabsTrigger>
                    <TabsTrigger value="LINE_ITEM">Line item</TabsTrigger>
                  </TabsList>
                </div>

                <TabsContent value="PERCENTAGE" className="mt-5 space-y-3">
                  <div className="space-y-1">
                    <Typography variant="h5" as="span" className="font-semibold">
                      Milestone allocation
                    </Typography>
                    <Typography variant="body-sm" color="muted">
                      Name each milestone, set its issue and due dates, and drag or type
                      its share — the remaining milestones adjust automatically so the
                      split always totals 100%.
                    </Typography>
                  </div>
                  <div className="space-y-3">
                    {splitRows.map((row, index) => (
                      <div key={index} className="space-y-3 rounded-lg border p-4">
                        <div className="grid grid-cols-1 gap-x-6 gap-y-3 md:grid-cols-3">
                          <div className="space-y-1.5">
                            <Label htmlFor={`split-label-${index}`}>Label</Label>
                            <Input
                              id={`split-label-${index}`}
                              value={row.label}
                              onChange={(changeEvent) =>
                                updateRowLabel(index, changeEvent.target.value)
                              }
                            />
                          </div>
                          <div className="space-y-1.5">
                            <Label htmlFor={`split-issue-date-${index}`}>
                              Issue date
                            </Label>
                            <DatePicker
                              id={`split-issue-date-${index}`}
                              value={row.issueDate}
                              onChange={(nextDate) => updateRowIssueDate(index, nextDate)}
                              required
                            />
                          </div>
                          <div className="space-y-1.5">
                            <Label htmlFor={`split-due-date-${index}`}>Due date</Label>
                            <DatePicker
                              id={`split-due-date-${index}`}
                              value={row.dueDate}
                              onChange={(nextDate) => updateRowDueDate(index, nextDate)}
                              required
                            />
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            disabled={percentages[index] <= 1}
                            aria-label={`Decrease split ${index + 1} percentage`}
                            onClick={() =>
                              handlePercentageDrag(index, percentages[index] - 1)
                            }
                          >
                            <Minus className="h-4 w-4" aria-hidden="true" />
                          </Button>
                          <Slider
                            value={[percentages[index]]}
                            min={1}
                            max={99}
                            step={1}
                            onValueChange={([nextValue]) =>
                              handlePercentageDrag(index, nextValue)
                            }
                            aria-label={`Split ${index + 1} percentage`}
                            className="flex-1"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            disabled={percentages[index] >= 99}
                            aria-label={`Increase split ${index + 1} percentage`}
                            onClick={() =>
                              handlePercentageDrag(index, percentages[index] + 1)
                            }
                          >
                            <Plus className="h-4 w-4" aria-hidden="true" />
                          </Button>
                          <span className="w-10 shrink-0 text-right text-sm tabular-nums">
                            {percentages[index]}%
                          </span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            disabled={!canRemovePercentageSplit}
                            aria-label={`Remove split ${index + 1}`}
                            onClick={() => handleRemovePercentageSplit(index)}
                          >
                            <X className="h-4 w-4" aria-hidden="true" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center justify-between">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={!canAddPercentageSplit}
                      onClick={handleAddPercentageSplit}
                    >
                      <Plus className="h-4 w-4" aria-hidden="true" />
                      Add split
                    </Button>
                    <span role="status" className="text-muted-foreground text-sm">
                      Total: {percentageTotal}%
                    </span>
                  </div>
                </TabsContent>

                <TabsContent value="LINE_ITEM" className="mt-5 space-y-3">
                  <div className="space-y-1">
                    <Typography variant="h5" as="span" className="font-semibold">
                      Milestone allocation
                    </Typography>
                    <Typography variant="body-sm" color="muted">
                      Name each milestone, set its issue and due dates, and enter how many
                      units of each line item belong to it — quantities must add up to the
                      full line item.
                    </Typography>
                  </div>
                  <div className="overflow-x-auto rounded-md border">
                    <table className="w-full min-w-[42rem] text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="bg-background sticky left-0 border-r px-3 py-2 text-left font-medium">
                            Line item
                          </th>
                          {splitRows.map((row, index) => (
                            <th
                              key={index}
                              className="border-r px-3 py-2 text-left font-medium last:border-r-0"
                            >
                              <div className="flex items-center gap-2">
                                <Label
                                  htmlFor={`split-label-li-${index}`}
                                  className="sr-only"
                                >
                                  Label
                                </Label>
                                <Input
                                  id={`split-label-li-${index}`}
                                  value={row.label}
                                  placeholder={defaultRowLabel(index)}
                                  onChange={(changeEvent) =>
                                    updateRowLabel(index, changeEvent.target.value)
                                  }
                                  className="h-8 min-w-[7rem]"
                                />
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6 shrink-0"
                                  disabled={splitRows.length <= MIN_SPLITS}
                                  aria-label={`Remove split ${index + 1}`}
                                  onClick={() => handleRemoveSplitRow(index)}
                                >
                                  <X className="h-3.5 w-3.5" aria-hidden="true" />
                                </Button>
                              </div>
                              <div className="mt-2 space-y-2">
                                <div>
                                  <Label
                                    htmlFor={`split-issue-date-li-${index}`}
                                    className="sr-only"
                                  >
                                    Issue date
                                  </Label>
                                  <DatePicker
                                    id={`split-issue-date-li-${index}`}
                                    value={row.issueDate}
                                    onChange={(nextDate) =>
                                      updateRowIssueDate(index, nextDate)
                                    }
                                    placeholder="Issue date"
                                    className="h-8 w-full"
                                    required
                                  />
                                </div>
                                <div>
                                  <Label
                                    htmlFor={`split-due-date-li-${index}`}
                                    className="sr-only"
                                  >
                                    Due date
                                  </Label>
                                  <DatePicker
                                    id={`split-due-date-li-${index}`}
                                    value={row.dueDate}
                                    onChange={(nextDate) =>
                                      updateRowDueDate(index, nextDate)
                                    }
                                    placeholder="Due date"
                                    className="h-8 w-full"
                                    required
                                  />
                                </div>
                              </div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {lineItems.map((item) => {
                          const allocation =
                            allocations[item.id] ??
                            evenQuantitySplit(item.quantity, splitRows.length)
                          const allocatedTotal = allocation.reduce(
                            (sum, value) => sum + value,
                            0,
                          )
                          const isBalanced =
                            Math.abs(allocatedTotal - item.quantity) < 0.0001
                          return (
                            <tr key={item.id} className="border-b last:border-b-0">
                              <td className="bg-background sticky left-0 border-r px-3 py-2 whitespace-nowrap">
                                <div>{item.title}</div>
                                <div
                                  className={cn(
                                    'text-xs',
                                    isBalanced
                                      ? 'text-muted-foreground'
                                      : 'text-destructive',
                                  )}
                                >
                                  {allocatedTotal} / {item.quantity} allocated
                                </div>
                              </td>
                              {splitRows.map((_, index) => (
                                <td
                                  key={index}
                                  className="border-r px-3 py-2 last:border-r-0"
                                >
                                  <QuantitySelector
                                    id={`split-allocation-${item.id}-${index}`}
                                    value={allocation[index] ?? 0}
                                    onChange={(nextValue) =>
                                      handleAllocationChange(item.id, index, nextValue)
                                    }
                                    min={0}
                                    step={getQuantityStepForUnitType(item.unitType)}
                                    ariaLabel={`${item.title} quantity for split ${index + 1}`}
                                  />
                                </td>
                              ))}
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={splitRows.length >= MAX_SPLITS}
                    onClick={handleAddSplitRow}
                  >
                    <Plus className="h-4 w-4" aria-hidden="true" />
                    Add split
                  </Button>
                </TabsContent>
              </Tabs>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => handleOpenChange(false)}>
                Cancel
              </Button>
              <Button onClick={() => setStep('confirm')} disabled={!canReview}>
                <Split className="mr-1.5 size-3.5" aria-hidden="true" />
                Review split
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Review and confirmation</DialogTitle>
              <DialogDescription>
                Once split, quote {quote.displayNumber} moves to Invoiced (Draft) and
                can&apos;t be undone.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3 overflow-y-auto py-2">
              <Typography variant="body-sm">
                {splitRows.length} invoices will be created, each linking back to this
                quote:
              </Typography>
              <div className="overflow-x-auto rounded-md border">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="border-r px-3 py-2 text-left font-medium">
                        Milestone
                      </th>
                      <th className="border-r px-3 py-2 text-left font-medium">Label</th>
                      {mode === 'PERCENTAGE' && (
                        <th className="border-r px-3 py-2 text-right font-medium">
                          Share
                        </th>
                      )}
                      <th className="border-r px-3 py-2 text-left font-medium">
                        Issue date
                      </th>
                      <th className="px-3 py-2 text-left font-medium">Due date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {splitRows.map((row, index) => (
                      <tr key={index} className="border-b last:border-b-0">
                        <td className="text-muted-foreground border-r px-3 py-2 whitespace-nowrap">
                          {index + 1} of {splitRows.length}
                        </td>
                        <td className="border-r px-3 py-2">{row.label}</td>
                        {mode === 'PERCENTAGE' && (
                          <td className="border-r px-3 py-2 text-right tabular-nums">
                            {percentages[index]}%
                          </td>
                        )}
                        <td className="border-r px-3 py-2 whitespace-nowrap">
                          {format(parseISO(row.issueDate!), 'PPP')}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          {format(parseISO(row.dueDate!), 'PPP')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Alert>
                <Info className="h-4 w-4" aria-hidden="true" />
                <AlertDescription className="space-y-2">
                  {showPercentageRoundingNotice && (
                    <p>
                      Because whole-percent shares rarely divide the total evenly, the
                      last invoice in the list absorbs the rounding remainder so the split
                      reconciles exactly to the quote total — its amount may be a few
                      cents higher or lower than its nominal percentage share.
                    </p>
                  )}
                  <p>
                    The generated invoices are otherwise normal DRAFT invoices — each can
                    be individually edited, sent, or deleted like any other invoice, but
                    doing so won&apos;t rebalance its siblings.
                  </p>
                </AlertDescription>
              </Alert>
              <RemindersSection
                checked={remindersEnabled}
                onCheckedChange={setRemindersEnabled}
                clientId={quote.clientId}
                scheduleId={reminderScheduleId}
                onScheduleChange={setReminderScheduleId}
                dueDate={null}
                variant="split"
              />
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setStep('configure')}
                disabled={mutation.isPending}
              >
                Back
              </Button>
              <Button onClick={handleSubmit} disabled={mutation.isPending}>
                <Receipt className="mr-1.5 size-3.5" aria-hidden="true" />
                {mutation.isPending ? 'Splitting…' : 'Split into Invoices'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
